import { useEffect, useRef, useState } from 'react'
import useZipcode from 'src/contexto/hooks/useZipcode'
import { apiV2 } from 'src/services/api'
import { autenticado } from 'src/services/auth'
import { CookieKeyTypeEnum, getCookieItem } from 'src/util/cookie-domain'
import { mascaraCep } from 'src/util/formatar'
import Carregando from '../carregando'
import { hiddenCart } from '../carrinho'
import GConfirmButton from '../gButtons/gConfirmButton'
import GInputText from '../gInput/gInputText'
import * as Yup from 'yup'

import {
  Container,
  ContentBox,
  InputContainer,
  MenuDescription,
  Text,
  Title,
  TypedContainer,
} from './styles'

import { IconLocation, useBreakpointValue } from '@gazin/ecommerce-ui'
import { getCustomerAddresses } from '../../services/customerAddresses'
import { ZipcodeAddress } from './types'
import ZipcodeBox from './zipcodeBox'

type Props = {
  isActive: boolean
  setIsActive: (value: boolean) => void
  textColor?: string
}

const zipcodeSchema = Yup.string()
  .required('O CEP é obrigatório')
  .matches(/^\d{5}-\d{3}$/, 'CEP inválido')

const InformeCep = ({ isActive, setIsActive, textColor }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [addresses, setAddresses] = useState<ZipcodeAddress[]>([])
  const [zipcodeValue, setZipcodeValue] = useState<string | undefined>(
    getCookieItem(CookieKeyTypeEnum.cep)
  )
  const [isInvalidZipcode, setIsInvalidZipcode] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const zipcodeContext = useZipcode()

  const isKeyDown = useRef(false)

  const timeoutId = useRef<ReturnType<typeof setTimeout>>()

  const isDesktop = Boolean(
    useBreakpointValue({ base: false, md: false, lg: true })
  )

  useEffect(() => {
    setIsAuthenticated(Boolean(autenticado()))
  }, [])

  useEffect(() => {
    if (zipcodeContext.zipcode) {
      setZipcodeValue(zipcodeContext.zipcode)
      setIsInvalidZipcode(false)
      setValidationError(null)
    }
  }, [zipcodeContext.zipcode])

  useEffect(() => {
    if (!isAuthenticated) return
    setIsLoading(true)
    getCustomerAddresses()
      .then((response) => {
        setAddresses(response.result)

        if ((!zipcodeValue || !zipcodeValue.length) && response.result) {
          setTimeout(() => setIsActive(false), 4000)

          const defaultZipcode = response.result.find(
            (address) => address.padrao
          )?.cep

          if (defaultZipcode) {
            setZipcodeValue(mascaraCep(defaultZipcode))
            zipcodeContext.set(mascaraCep(defaultZipcode))
          }
        }
      })
      .catch(() => null)
      .finally(() => setIsLoading(false))
  }, [isAuthenticated])

  useEffect(() => {
    if (typeof zipcodeValue === 'undefined') {
      setIsActive(isDesktop)
    }
    if (!isAuthenticated) {
      timeoutId.current = setTimeout(() => {
        !zipcodeValue && !isKeyDown.current && setIsActive(false)
      }, 4000)
    }

    return () => {
      clearTimeout(timeoutId.current!)
    }
  }, [isAuthenticated, isDesktop])

  const active = () => {
    setIsActive(true)
    hiddenCart()

    document.querySelector('.overlay')?.classList.add('active')
  }

  const hidden = () => {
    setIsActive(false)

    document.querySelector('.overlay')?.classList.remove('active')
  }

  const onSubmit = async (zipcode?: string) => {
    setIsLoading(true)
    setValidationError(null)

    const zipcodeToUse = typeof zipcode === 'string' ? zipcode : zipcodeValue

    try {
      await zipcodeSchema.validate(zipcodeToUse, { abortEarly: false })
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setValidationError(error.message)
        setIsInvalidZipcode(true)
        setIsLoading(false)
        active()
        return
      }
    }

    apiV2
      .get(
        `${process.env.REACT_APP_API_URI_V2}/canais/cep/${zipcodeToUse?.replace(
          /\D/g,
          ''
        )}`
      )
      .then((response) => {
        if (!response.data) {
          setIsInvalidZipcode(true)
          setValidationError('CEP não encontrado')
          active()
          return
        }

        setZipcodeValue(zipcodeToUse)
        zipcodeContext.set(mascaraCep(zipcodeToUse))
        setIsInvalidZipcode(false)
        setValidationError(null)
        hidden()
      })
      .catch(() => {
        setIsInvalidZipcode(true)
        setValidationError('CEP inválido')
        active()
      })
      .finally(() => setIsLoading(false))
  }

  const onClear = () => {
    setZipcodeValue(undefined)
    zipcodeContext.remove()
    setIsInvalidZipcode(false)
    setValidationError(null)
  }

  return (
    <>
      {isLoading && <Carregando />}
      <Container id="informeCep" onClick={active}>
        <MenuDescription onMouseOver={active}>
          <IconLocation
            width="1.1875rem"
            height="1.1875rem"
            color={textColor || '#fff'}
          />

          <Text textColor={textColor || '#fff'}>
            {zipcodeContext.zipcode
              ? `CEP ${zipcodeContext.zipcode}`
              : 'Informe seu CEP'}
          </Text>
        </MenuDescription>
        <ContentBox isActive={isActive} onMouseLeave={hidden}>
          <TypedContainer>
            <Title>Veja as melhores formas de entrega para o seu CEP.</Title>
            <InputContainer>
              <GInputText
                widht={169}
                height={36.65}
                value={zipcodeValue || ''}
                setValue={setZipcodeValue}
                placeholder="Digite seu CEP"
                mask="99999-999"
                onSubmit={onSubmit}
                isInvalidField={isInvalidZipcode}
                setIsInvalidZipcode={setIsInvalidZipcode}
                errorText={validationError || 'CEP inválido'}
                buttonType="close"
                onButtonClick={onClear}
                onKeyPress={() => {
                  isKeyDown.current = true
                }}
              />
              <GConfirmButton
                height={37}
                width={37}
                onSubmit={onSubmit}
                margin={'0 0 0 .5rem'}
              />
            </InputContainer>
          </TypedContainer>
          <ZipcodeBox
            addresses={addresses}
            setZipcodeValue={setZipcodeValue}
            onSubmit={onSubmit}
            isAuthenticated={isAuthenticated}
          />
        </ContentBox>
      </Container>
    </>
  )
}

export default InformeCep
